import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrendingUp, TrendingDown, Activity, Heart, Zap } from 'lucide-react';

export const PerformanceCorrelationInsights: React.FC = () => {
  const { user } = useAuth();

  const { data: correlationData, isLoading } = useQuery({
    queryKey: ['performance-correlations', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Fetch recent running data
      const { data: runs } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .not('data_source', 'is', null)
        .order('recorded_date', { ascending: false })
        .limit(30);

      if (!runs || runs.length === 0) return null;

      // Calculate Strava-based insights
      const insights = [];

      // Strava suffer score analysis
      const stravaRuns = runs.filter(r => r.data_source === 'strava' && r.suffer_score);
      if (stravaRuns.length > 5) {
        const sufferScoreTrend = calculateSufferScoreTrend(stravaRuns);
        if (sufferScoreTrend) insights.push(sufferScoreTrend);
      }

      // Heart rate consistency analysis
      const hrRuns = runs.filter(r => r.heart_rate_avg && r.heart_rate_max);
      if (hrRuns.length > 5) {
        const hrInsight = calculateHeartRateConsistency(hrRuns);
        if (hrInsight) insights.push(hrInsight);
      }

      // Pace consistency analysis
      if (runs.length > 5) {
        const paceInsight = calculatePaceConsistency(runs);
        if (paceInsight) insights.push(paceInsight);
      }

      return insights;
    },
    enabled: !!user,
  });

  const calculateSufferScoreTrend = (runs: any[]) => {
    const recentRuns = runs.slice(0, 10);
    const olderRuns = runs.slice(-10);
    
    const recentAvgSuffer = recentRuns.reduce((sum, r) => sum + (r.suffer_score || 0), 0) / recentRuns.length;
    const olderAvgSuffer = olderRuns.reduce((sum, r) => sum + (r.suffer_score || 0), 0) / olderRuns.length;
    
    const sufferTrend = ((recentAvgSuffer - olderAvgSuffer) / olderAvgSuffer) * 100;
    
    if (Math.abs(sufferTrend) > 15) {
      return {
        type: 'suffer_score',
        title: 'Training Intensity Trend',
        impact: sufferTrend > 0 ? 'negative' : 'positive',
        message: sufferTrend > 0 
          ? `Recent runs show ${sufferTrend.toFixed(1)}% higher suffer scores - consider recovery`
          : `Training stress has decreased by ${Math.abs(sufferTrend).toFixed(1)}% - good recovery`,
        icon: Zap,
        strength: Math.abs(sufferTrend) > 25 ? 'strong' : 'moderate'
      };
    }
    
    return null;
  };

  const calculateHeartRateConsistency = (runs: any[]) => {
    const hrEfficiency = runs.map(run => ({
      efficiency: (run.heart_rate_avg / run.heart_rate_max) * 100,
      pace: run.pace_per_km_seconds
    }));
    
    const avgEfficiency = hrEfficiency.reduce((sum, r) => sum + r.efficiency, 0) / hrEfficiency.length;
    const efficiencyVariance = hrEfficiency.reduce((sum, r) => sum + Math.pow(r.efficiency - avgEfficiency, 2), 0) / hrEfficiency.length;
    
    if (efficiencyVariance < 25) {
      return {
        type: 'hr_consistency',
        title: 'Heart Rate Efficiency',
        impact: 'positive',
        message: `Consistent heart rate efficiency (${avgEfficiency.toFixed(1)}%) shows good aerobic fitness`,
        icon: Heart,
        strength: 'moderate'
      };
    } else if (efficiencyVariance > 50) {
      return {
        type: 'hr_consistency',
        title: 'Heart Rate Variability',
        impact: 'negative',
        message: `High heart rate variability suggests inconsistent effort or recovery needs`,
        icon: Heart,
        strength: 'moderate'
      };
    }
    
    return null;
  };

  const calculatePaceConsistency = (runs: any[]) => {
    const paceData = runs.filter(r => r.pace_per_km_seconds).slice(0, 10);
    if (paceData.length < 5) return null;
    
    const avgPace = paceData.reduce((sum, r) => sum + r.pace_per_km_seconds, 0) / paceData.length;
    const paceVariance = paceData.reduce((sum, r) => sum + Math.pow(r.pace_per_km_seconds - avgPace, 2), 0) / paceData.length;
    const paceCV = Math.sqrt(paceVariance) / avgPace;
    
    if (paceCV < 0.1) {
      return {
        type: 'pace_consistency',
        title: 'Pace Consistency',
        impact: 'positive',
        message: `Excellent pace consistency - your training is paying off!`,
        icon: Activity,
        strength: 'strong'
      };
    } else if (paceCV > 0.2) {
      return {
        type: 'pace_consistency',
        title: 'Pace Variability',
        impact: 'negative',
        message: `High pace variability - consider more structured training`,
        icon: Activity,
        strength: 'moderate'
      };
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!correlationData || correlationData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Not enough data for correlation analysis. Keep tracking your runs to unlock Strava-based insights!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Performance Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {correlationData.map((insight, index) => (
          <div key={index} className="flex items-start gap-3 p-3 rounded-lg border">
            <div className={`p-2 rounded-lg ${
              insight.impact === 'positive' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
            }`}>
              <insight.icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium">{insight.title}</h4>
                <Badge variant={insight.impact === 'positive' ? 'default' : 'secondary'}>
                  {insight.strength === 'strong' ? 'Strong' : 'Moderate'} correlation
                </Badge>
                {insight.impact === 'positive' ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-orange-600" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">{insight.message}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};